import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useSelector, connect, useDispatch } from 'react-redux';
import { editDeviceSave } from 'commons/src/actions/DeviceActions';
import { analyticsLogger, PageType } from 'commons/src/analytics';
import { DEVICE_SAVED_SETTINGS } from 'commons/src/analytics/AnalyticsEvents';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { DeviceType } from 'commons/src/models/commonTypeScript';
import { CommonRequestType } from 'commons/src/reducers/requestReducer';
import { AddZoneGroupPayload } from '../../../models/common';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import AddGroupForm from '../../buildings/deviceGroup/AddGroupForm';

type StateProps = {
    loading: boolean;
    error?: string;
    rfRegionValidationLoading: boolean;
};

export type PassedProps = {
    serialNumber: string;
    onClose: () => void;
    closeOnSave?: boolean;
};

export type Props = PassedProps & StateProps;

export const DeviceDetailsComponent = ({
    error,
    onClose,
    loading,
    serialNumber,
    rfRegionValidationLoading,
    closeOnSave,
}: Props): React.ReactElement => {
    const dispatch = useDispatch();
    const { virtualDevices } = useSelector((state: Store) => state.virtualDevices);
    const { devices } = useSelector((state: Store) => state.devices);
    const device: DeviceType | undefined = virtualDevices?.[serialNumber] || devices?.[serialNumber];

    const [isLoading, setIsLoading] = useState(false);
    const [triggerSubmitVirtualDevices, setTriggerSubmitVirtualDevices] = useState(false);

    useEffect((): void => {
        if (error || !loading) {
            setIsLoading(false);
            if (closeOnSave) {
                onClose();
            }
        }
    }, [error, loading]);

    const submit = (e: SyntheticEvent<HTMLElement>): void => {
        e.preventDefault();
        setTriggerSubmitVirtualDevices(true);
    };

    const submitVirtualDevice = (payload: AddZoneGroupPayload): void => {
        if (triggerSubmitVirtualDevices) {
            const virtualDevicePayload = {
                locationName: device?.locationName,
                name: payload.segmentName,
                metaData: { ...payload.deviceMetaData, members: payload.members },
                serialNumber,
                segmentId: device.segmentId,
            };
            dispatch(editDeviceSave(virtualDevicePayload));
            analyticsLogger(DEVICE_SAVED_SETTINGS, {
                pageType: PageType.Device,
                deviceSegmentProps: virtualDevicePayload,
            });
        }
    };

    return (
        <div className="page-wrapper__inner page-wrapper__inner--slim">
            <form className="change-location__form">
                <AddGroupForm
                    locationId={device?.locationId}
                    selectedDevice={device}
                    triggerSubmit={triggerSubmitVirtualDevices}
                    setPayload={submitVirtualDevice}
                    setTriggerSubmit={setTriggerSubmitVirtualDevices}
                    slimView
                />
                {error && <ResponseBox text={`ErrorCodes.${error}`} />}
                <div className="change-location__form__buttons">
                    <PrimaryButton color="secondary" id="close" title="Close" loading={false} onClick={onClose} />
                    <PrimaryButton
                        id="submit"
                        type="submit"
                        title="SaveChanges"
                        color="primary"
                        loading={isLoading}
                        onClick={submit}
                        disabled={rfRegionValidationLoading}
                    />
                </div>
            </form>
        </div>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        devicePage: { updateSegmentDetailsError, loading },
        requests: {
            [RequestType.UpdateSegmentProperties]: { error },
        },
        commonRequests: {
            [CommonRequestType.ValidateLocationRfRegion]: { loading: rfRegionValidationLoading },
        },
    } = state;

    return {
        error: (error && error.error) || updateSegmentDetailsError,
        loading,
        rfRegionValidationLoading,
    };
};

export default connect(mapStateToProps)(DeviceDetailsComponent);
